import React from 'react';
import {
  Box,
  InputBaseProps,
  Stack,
  TextField,
  TextFieldProps,
  TextFieldVariants,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

export interface FormFieldUnderlineProps
  extends Omit<
    {
      /**
       * The variant to use.
       * @default 'outlined'
       */
      variant?: TextFieldVariants;
    } & Omit<TextFieldProps, 'variant'>,
    'type'
  > {
  icon?: React.ReactNode;
  type?: InputBaseProps['type'];
}

export const FormFieldUnderline = React.forwardRef<
  HTMLInputElement,
  FormFieldUnderlineProps
>(({ icon, label, error: hasError, type = 'text', ...props }, ref) => {
  const theme: Theme = useTheme();
  const innerRef = React.useRef<HTMLInputElement>(null);
  const commonProps = {
    ...props,
    inputRef: ref && 'current' in ref ? ref : innerRef,
    sx: { mt: 0.5, ...props.sx },
  };

  const handleClick = () => {
    commonProps.inputRef?.current?.focus?.();
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      justifyContent="space-between"
      sx={{ width: '100%' }}
    >
      {icon ? (
        <Box onClick={handleClick} sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {icon}
        </Box>
      ) : null}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          width: '100%',
          position: 'relative',
          borderBottom: `1px solid ${theme.palette.text.secondary}`,
          '&:after, &:before': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '2px',
            bottom: 0,
            left: 0,
            right: 0,
            transform: 'scaleX(0)',
            transformOrigin: 'bottom center',
            transition: 'transform 0.25s ease-out',
          },
          '&:hover:after, &:focus-within:after': {
            transform: 'scaleX(1)',
            borderBottom: `1px solid ${theme.palette.text.secondary}`,
            transformOrigin: 'bottom center',
          },
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
            paddingBottom: '4px',
            whiteSpace: 'nowrap',
          }}
          onClick={handleClick}
        >
          {label}
        </Typography>
        <TextField
          {...commonProps}
          type={type}
          variant="standard"
          fullWidth
          sx={{
            input: {
              textAlign: 'end',
            },
          }}
          slotProps={{
            input: {
              disableUnderline: true,
              sx: {
                '& input[type=number]': {
                  '-moz-appearance': 'textfield',
                },
                '& input[type=number]::-webkit-outer-spin-button': {
                  '-webkit-appearance': 'none',
                  margin: 0,
                },
                '& input[type=number]::-webkit-inner-spin-button': {
                  '-webkit-appearance': 'none',
                  margin: 0,
                },
              },
            },
          }}
        />
      </Stack>
    </Stack>
  );
});

FormFieldUnderline.displayName = 'FormFieldUnderline';
